import React, { MouseEventHandler, useCallback } from 'react';

import { useAtom } from 'jotai/index';

import { cookieJar, uiRoutes } from '@consumer-frontend/api';
import { resetAnalytics } from '@consumer-frontend/shared';
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Icon,
  ListGroupItem,
  NavLink,
} from '@consumer-frontend/shared-ui';

import { showMobileNavAtom } from '../../store';

import '@consumer-bootstrap/index.scss';
import '@consumer-bootstrap/pages/account-home/nav.scss';

export type BlocLocNavPropsT = {
  showFeeSaverStatus: boolean;
  openLoanNumber?: string;
};

export type BlocLocNavT = React.FC<BlocLocNavPropsT>;

export const BlocLocNav: BlocLocNavT = ({
  openLoanNumber, showFeeSaverStatus,
}) => {
  const [, setShowMobileNav] = useAtom(showMobileNavAtom);

  const closeMobileNav: MouseEventHandler = useCallback(
    (ev) => {
      (ev?.currentTarget as HTMLElement)?.blur();

      setShowMobileNav(false);
    },
    [setShowMobileNav],
  );

  const handleSignout = useCallback(
    () => {
      cookieJar.expire();
      resetAnalytics();
      setShowMobileNav(false);
    },
    [setShowMobileNav],
  );

  const contractsLink = openLoanNumber
    ? uiRoutes.legacyContract(openLoanNumber)
    : '';

  return (
    <>
      <ListGroupItem>
        <NavLink to={uiRoutes.blocAccountSummary} onClick={closeMobileNav}>
          <Icon iconName="house-door" />
          <span data-text="My Account">My Account</span>
        </NavLink>
      </ListGroupItem>
      <ListGroupItem>
        <NavLink to={uiRoutes.blocMakePayment} onClick={closeMobileNav}>
          <Icon iconName="currency-dollar" />
          <span>Make a Payment</span>
        </NavLink>
        {openLoanNumber && (
          <NavLink to={uiRoutes.blocCashAdvance(openLoanNumber)} onClick={closeMobileNav}>
            <Icon iconName="cash" />
            <span>Request a Cash Advance</span>
          </NavLink>
        )}
        <NavLink to={uiRoutes.blocLoanDetails} onClick={closeMobileNav}>
          <Icon iconName="calculator" />
          <span>Minimum Payment Estimator</span>
        </NavLink>
        {showFeeSaverStatus && openLoanNumber && (
          <NavLink to={uiRoutes.blocFeeSaverStatus(openLoanNumber)} onClick={closeMobileNav}>
            <Icon iconName="coin" />
            <span>Fee Saver Status</span>
          </NavLink>
        )}
      </ListGroupItem>
      <ListGroupItem>
        {openLoanNumber && (
          <>
            <NavLink to={uiRoutes.blocStatements(openLoanNumber)} onClick={closeMobileNav}>
              <Icon iconName="file-ruled" />
              <span>View Statements</span>
            </NavLink>
            <NavLink to={contractsLink} onClick={closeMobileNav}>
              <Icon iconName="file-earmark-text" />
              <span>View Contract</span>
            </NavLink>
          </>
        )}
      </ListGroupItem>
      <ListGroupItem>
        <Dropdown>
          <DropdownToggle as="button" className="nav-link">
            <Icon iconName="person" />
            <span>My Info</span>
          </DropdownToggle>
          <DropdownMenu>
            <DropdownItem
              className="nav-link"
              to={uiRoutes.blocPersonalInfo}
              onClick={closeMobileNav}
            >
              <span>Personal Info</span>
            </DropdownItem>
            <DropdownItem
              className="nav-link"
              to={uiRoutes.blocBankInfo}
              onClick={closeMobileNav}
            >
              <span>Bank Info</span>
            </DropdownItem>
            <DropdownItem
              className="nav-link"
              to={uiRoutes.blocEmploymentInfo}
              onClick={closeMobileNav}
            >
              <span>Employment Info</span>
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
        <NavLink to={uiRoutes.messages} onClick={closeMobileNav}>
          <Icon iconName="envelope" />
          <span>Message Center</span>
        </NavLink>
        <NavLink to={uiRoutes.contactUs} onClick={closeMobileNav}>
          <Icon iconName="chat-left" />
          <span>Contact Us</span>
        </NavLink>
      </ListGroupItem>
      <ListGroupItem>
        <NavLink to={uiRoutes.blocLogOut} onClick={handleSignout}>
          <Icon iconName="box-arrow-right" />
          <span>Sign Out</span>
        </NavLink>
      </ListGroupItem>
    </>
  );
};
